import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import {
  $profileStore,
  $transferRequestsApprovedListStore,
  $transferRequestsGridListStore,
  $transferRequestsLimitsStore,
  $transferUpdateStatusStore,
} from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { TransferRequestStatusEnum, UserType, VopStatus, type TransferRequestResponse } from '@shared/api';
import { TransferTable } from './transferTable';
import { ConfirmModal, OTPModal, RecipientVerificationModal, TransferRequestConfirmModal, VerifiedRecipientModal } from '@widgets';
import { Spinner, Text } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

interface TransferRequestsProps {
  className?: string;
}

export const TransferRequests = (props: TransferRequestsProps) => {
  const { navigate } = useRouteHook();

  const profileStore = useUnit($profileStore.store);
  const transferStore = useUnit($transferRequestsGridListStore.store);
  const transferRequestLimit = useUnit($transferRequestsLimitsStore.store);
  const transferUpdateStatusStore = useUnit($transferUpdateStatusStore.store);

  const [selectedTransfer, setSelectedTransfer] = useState<TransferRequestResponse | null>(null);
  const [showRequestConfirmationModal, setShowRequestConfirmationModal] = useState<boolean>(false);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);
  const paymentsRequestsState = useListState({ list: 'paymentsRequests' });

  const { t } = useTranslation('payments');

  useEffect(() => {
    $transferRequestsLimitsStore.events.getLimits();
    return () => {
      $transferRequestsGridListStore.events.clear();
      $transferRequestsLimitsStore.events.clear();
      $transferUpdateStatusStore.events.clear();
    };
  }, []);

  useEffect(() => {
    $transferRequestsGridListStore.events.getTransfers({ Count: paymentsRequestsState.count, FromIndex: paymentsRequestsState.fromIndex });
  }, [paymentsRequestsState]);

  const updateDataHandler = () => {
    paymentsRequestsState.onChangePage(1);
    Promise.allSettled([
      $transferRequestsGridListStore.events.getTransfers({ Count: paymentsRequestsState.count, FromIndex: 0 }),
      $transferRequestsApprovedListStore.events.getTransfers({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 }),
    ]);
  };

  const submitHandler = async (vopForceUpdate?: boolean) => {
    if (!selectedTransfer) return;
    await $transferUpdateStatusStore.events.updateStatus({
      id: selectedTransfer.id,
      status: TransferRequestStatusEnum.WaitingForApproval,
      vopForceUpdate,
    }),
      await Promise.allSettled([
        $transferRequestsGridListStore.events.getTransfers({ Count: paymentsRequestsState.count, FromIndex: paymentsRequestsState.fromIndex }),
        $transferRequestsApprovedListStore.events.getTransfers({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 }),
      ]);
    setShowRequestConfirmationModal(false);
  };

  const cancelHandler = async () => {
    if (!selectedTransfer) return;
    await $transferUpdateStatusStore.events.updateStatus({ id: selectedTransfer.id, status: TransferRequestStatusEnum.Cancelled });
    updateDataHandler();
  };
  const submitForceUpdateHandler = async () => await submitHandler(true);

  return (
    <div
      className={cn(
        'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <Spinner show={transferUpdateStatusStore.loading && !!transferUpdateStatusStore.error?.errorCode} position />
      <Text weight='semibold' size={PaletteUtils.isInsigniaPalette ? '2xl' : 'lg'}>
        {t('transferRequest.title')}
      </Text>
      <TransferTable
        isLoading={transferStore.loading}
        error={transferStore.error}
        onRefresh={() =>
          $transferRequestsGridListStore.events.getTransfers({ Count: paymentsRequestsState.count, FromIndex: paymentsRequestsState.fromIndex })
        }
        data={transferStore.data.data}
        totalItemCount={transferStore.data.total}
        currentPage={paymentsRequestsState.page}
        countItemPage={paymentsRequestsState.count}
        onChangePage={paymentsRequestsState.onChangePage}
        onChangeCountItemPage={paymentsRequestsState.onChangeCount}
        setSelectedTransfer={(data) => (setSelectedTransfer(data), setShowRequestConfirmationModal(true))}
      />
      {selectedTransfer && (
        <TransferRequestConfirmModal
          isOpen={showRequestConfirmationModal}
          onClose={() => setShowRequestConfirmationModal(false)}
          transactionId={selectedTransfer.id}
          transferData={selectedTransfer}
          onOpenOtpModal={
            selectedTransfer.status !== TransferRequestStatusEnum.Draft
              ? (data) => (setShowOtpModal(data), setShowRequestConfirmationModal(false))
              : undefined
          }
          cancelLabel={
            selectedTransfer.status === TransferRequestStatusEnum.WaitingForApproval
              ? t('modals.transferRequestConfirm.buttons.reject')
              : t('buttons.cancel', { ns: 'common' })
          }
          confirmLabel={
            selectedTransfer.status === TransferRequestStatusEnum.Draft
              ? t('buttons.send', { ns: 'common' })
              : t('modals.transferRequestConfirm.buttons.approve')
          }
          onSubmit={selectedTransfer.status === TransferRequestStatusEnum.Draft ? submitHandler : undefined}
          readOnly={
            !(
              selectedTransfer.status === TransferRequestStatusEnum.Draft ||
              (selectedTransfer.status === TransferRequestStatusEnum.WaitingForApproval && profileStore.data?.userType !== UserType.User)
            )
          }
          noDocumentUpload={!([TransferRequestStatusEnum.Draft] as TransferRequestStatusEnum[]).includes(selectedTransfer.status)}
          isLimitReached={selectedTransfer.senderAmount >= transferRequestLimit.data.outgoingTransferLimit}
          onUpdate={updateDataHandler}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        data={showOtpModal!}
        onSubmit={(data) => (setShowConfirmModal(data!), updateDataHandler())}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        onClose={() => setShowConfirmModal(null)}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='flex flex-col gap-2'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() =>
          showConfirmModal?.paymentId &&
          navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal.paymentId, { back: RoutePaths.payments.onNavigate() }))
        }
      />
      <VerifiedRecipientModal
        isOpen={
          transferUpdateStatusStore.data?.vopStatus === VopStatus.Verified &&
          transferUpdateStatusStore.data?.status === TransferRequestStatusEnum.WaitingForApproval
        }
        onClose={() => $transferUpdateStatusStore.events.clear()}
        buttonTitle='Continue payment'
      />
      <RecipientVerificationModal
        isOpen={!!transferUpdateStatusStore.error?.errorCode}
        onClose={() => $transferUpdateStatusStore.events.clear()}
        type='updateTransfer'
        error={transferUpdateStatusStore.error}
        onCancel={cancelHandler}
        onSubmit={submitForceUpdateHandler}
      />
    </div>
  );
};
